import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useDonorAuth } from '../context/DonorAuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Heart, FileText, CreditCard, Settings, LogOut,
  TrendingUp, ChevronRight, Loader2, CheckCircle, AlertCircle, Award, Download
} from 'lucide-react';

// Import refactored components
import {
  DonorStatsCards,
  DonationsTable,
  ReceiptsList,
  SubscriptionsList,
  PublicProfileForm,
  ProfileSettingsForm
} from '../components/donor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  fr: {
    welcome: 'Bienvenue',
    overview: 'Aperçu',
    donations: 'Mes dons',
    receipts: 'Reçus fiscaux',
    subscriptions: 'Dons récurrents',
    publicProfile: 'Profil public',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    totalDonated: 'Total des dons',
    totalDonations: 'Nombre de dons',
    activeSubscriptions: 'Abonnements actifs',
    receiptsAvailable: 'Reçus disponibles',
    memberSince: 'Membre depuis',
    recentDonations: 'Dons récents',
    viewAll: 'Voir tout',
    amount: 'Montant',
    date: 'Date',
    type: 'Type',
    status: 'Statut',
    single: 'Unique',
    monthly: 'Mensuel',
    completed: 'Complété',
    pending: 'En attente',
    downloadReceipt: 'Télécharger',
    resendReceipt: 'Renvoyer par courriel',
    noReceipts: 'Aucun reçu fiscal disponible',
    receiptsNote: 'Les reçus fiscaux sont émis pour les dons de 25$ et plus.',
    noDonations: 'Aucun don enregistré',
    startDonating: 'Faire un premier don',
    noSubscriptions: 'Aucun don récurrent actif',
    setupSubscription: 'Configurer un don mensuel',
    pauseSubscription: 'Mettre en pause',
    resumeSubscription: 'Reprendre',
    cancelSubscription: 'Annuler',
    nextPayment: 'Prochain paiement',
    profile: 'Mon profil',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    address: 'Adresse',
    city: 'Ville',
    postalCode: 'Code postal',
    saveChanges: 'Enregistrer',
    changePassword: 'Changer le mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer',
    updatePassword: 'Mettre à jour',
    profileUpdated: 'Profil mis à jour',
    passwordUpdated: 'Mot de passe modifié',
    passwordMismatch: 'Les mots de passe ne correspondent pas',
    receiptSent: 'Reçu envoyé par courriel',
    loading: 'Chargement...',
    publicProfileTitle: 'Profil public',
    publicProfileDesc: 'Choisissez d\'apparaître sur notre Mur d\'honneur pour inspirer les autres donateurs.',
    makePublic: 'Rendre mon profil public',
    publicVisible: 'Votre profil sera visible sur le Mur d\'honneur',
    privateHidden: 'Votre profil est privé',
    displayName: 'Nom à afficher',
    displayNameHint: 'Le nom qui apparaîtra sur le Mur d\'honneur',
    publicMessage: 'Message public (optionnel)',
    publicMessageHint: 'Partagez pourquoi vous soutenez notre cause',
    showAmount: 'Afficher le montant total de mes dons',
    showAmountHint: 'Votre total de dons sera visible publiquement',
    viewWallOfHonor: 'Voir le Mur d\'honneur',
    publicProfileUpdated: 'Profil public mis à jour',
  },
  en: {
    welcome: 'Welcome',
    overview: 'Overview',
    donations: 'My Donations',
    receipts: 'Tax Receipts',
    subscriptions: 'Recurring Donations',
    publicProfile: 'Public Profile',
    settings: 'Settings',
    logout: 'Log out',
    totalDonated: 'Total Donated',
    totalDonations: 'Number of Donations',
    activeSubscriptions: 'Active Subscriptions',
    receiptsAvailable: 'Receipts Available',
    memberSince: 'Member Since',
    recentDonations: 'Recent Donations',
    viewAll: 'View All',
    amount: 'Amount',
    date: 'Date',
    type: 'Type',
    status: 'Status',
    single: 'One-time',
    monthly: 'Monthly',
    completed: 'Completed',
    pending: 'Pending',
    downloadReceipt: 'Download',
    resendReceipt: 'Resend by email',
    noReceipts: 'No tax receipts available',
    receiptsNote: 'Tax receipts are issued for donations of $25 and above.',
    noDonations: 'No donations recorded',
    startDonating: 'Make your first donation',
    noSubscriptions: 'No active recurring donations',
    setupSubscription: 'Set up a monthly donation',
    pauseSubscription: 'Pause',
    resumeSubscription: 'Resume',
    cancelSubscription: 'Cancel',
    nextPayment: 'Next payment',
    profile: 'My Profile',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal Code',
    saveChanges: 'Save Changes',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm',
    updatePassword: 'Update',
    profileUpdated: 'Profile updated',
    passwordUpdated: 'Password changed',
    passwordMismatch: 'Passwords do not match',
    receiptSent: 'Receipt sent by email',
    loading: 'Loading...',
    publicProfileTitle: 'Public Profile',
    publicProfileDesc: 'Choose to appear on our Wall of Honor to inspire other donors.',
    makePublic: 'Make my profile public',
    publicVisible: 'Your profile will be visible on the Wall of Honor',
    privateHidden: 'Your profile is private',
    displayName: 'Display name',
    displayNameHint: 'The name that will appear on the Wall of Honor',
    publicMessage: 'Public message (optional)',
    publicMessageHint: 'Share why you support our cause',
    showAmount: 'Show my total donation amount',
    showAmountHint: 'Your donation total will be publicly visible',
    viewWallOfHonor: 'View Wall of Honor',
    publicProfileUpdated: 'Public profile updated',
  }
};

const DonorDashboardPage = () => {
  const { language } = useLanguage();
  const { donor, isAuthenticated, logout, getAuthHeaders } = useDonorAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [annualReceipts, setAnnualReceipts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [publicProfile, setPublicProfile] = useState({
    is_public: false,
    display_name: '',
    public_message: '',
    show_amount: false
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [updatingPublicProfile, setUpdatingPublicProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const text = translations[language];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, donationsRes, receiptsRes, profileRes, subsRes, publicProfileRes, annualRes] = await Promise.all([
        axios.get(`${API}/donor/stats`, { headers }),
        axios.get(`${API}/donor/donations`, { headers }),
        axios.get(`${API}/donor/receipts`, { headers }),
        axios.get(`${API}/donor/profile`, { headers }),
        axios.get(`${API}/donor/subscriptions`, { headers }),
        axios.get(`${API}/donor/public-profile`, { headers }),
        axios.get(`${API}/donor/annual-receipts`, { headers })
      ]);
      
      setStats(statsRes.data);
      setDonations(donationsRes.data.donations || []);
      setReceipts(receiptsRes.data.receipts || []);
      setAnnualReceipts(annualRes.data.years || []);
      setProfile(profileRes.data);
      setSubscriptions(subsRes.data.subscriptions || []);
      setPublicProfile(publicProfileRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/donor/login');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/donor/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate, loadData]);

  // Handlers
  const handlePublicProfileUpdate = async (e) => {
    e.preventDefault();
    setUpdatingPublicProfile(true);
    try {
      await axios.put(`${API}/donor/public-profile`, publicProfile, {
        headers: getAuthHeaders()
      });
      toast.success(text.publicProfileUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingPublicProfile(false);
    }
  };

  const handleDownloadReceipt = async (donationId) => {
    try {
      const response = await axios.get(`${API}/receipt/${donationId}`, {
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recu_Fiscal_${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDownloadAnnualReceipt = async (year, receiptNumber) => {
    try {
      const response = await axios.get(`${API}/donor/annual-receipts/${year}/pdf`, {
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recu_Annuel_${year}_${receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleResendReceipt = async (donationId) => {
    try {
      await axios.post(`${API}/receipt/resend/${donationId}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(text.receiptSent);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    try {
      await axios.put(`${API}/donor/profile`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        postal_code: profile.postal_code
      }, { headers: getAuthHeaders() });
      toast.success(text.profileUpdated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(text.passwordMismatch);
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/donor/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers: getAuthHeaders() });
      toast.success(text.passwordUpdated);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Formatters
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `${amount?.toFixed(2) || '0.00'}$ CAD`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A708B]" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: text.overview, icon: TrendingUp },
    { id: 'donations', label: text.donations, icon: Heart },
    { id: 'receipts', label: text.receipts, icon: FileText },
    { id: 'subscriptions', label: text.subscriptions, icon: CreditCard },
    { id: 'public', label: text.publicProfile, icon: Award },
    { id: 'settings', label: text.settings, icon: Settings },
  ];

  return (
    <div data-testid="donor-dashboard" className="min-h-screen bg-[#FAFAFA] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-[#1A252C]">
              {text.welcome}, {donor?.name?.split(' ')[0] || 'Donateur'}
            </h1>
            <p className="text-[#546A76] mt-1">{donor?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="mt-4 sm:mt-0 rounded-full border-[#E2E8F0]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {text.logout}
          </Button>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="bg-white rounded-2xl shadow-sm p-4 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#4A708B] text-white'
                      : 'text-[#546A76] hover:bg-[#F0F4F8]'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <DonorStatsCards stats={stats} text={text} formatAmount={formatAmount} />

                {/* Recent Donations */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-xl font-semibold text-[#1A252C]">
                      {text.recentDonations}
                    </h2>
                    <button 
                      onClick={() => setActiveTab('donations')}
                      className="text-[#4A708B] text-sm flex items-center gap-1 hover:underline"
                    >
                      {text.viewAll}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {donations.length > 0 ? (
                    <div className="space-y-3">
                      {donations.slice(0, 5).map((donation) => (
                        <div 
                          key={donation.id}
                          className="flex items-center justify-between p-4 rounded-xl bg-[#FAFAFA]"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              donation.payment_status === 'completed' ? 'bg-green-100' : 'bg-amber-100'
                            }`}>
                              {donation.payment_status === 'completed' 
                                ? <CheckCircle className="w-5 h-5 text-green-600" />
                                : <AlertCircle className="w-5 h-5 text-amber-600" />
                              }
                            </div>
                            <div>
                              <p className="font-medium text-[#1A252C]">
                                {formatAmount(donation.amount)}
                              </p>
                              <p className="text-sm text-[#546A76]">
                                {formatDate(donation.created_at)}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            donation.donation_type === 'monthly' 
                              ? 'bg-[#4A708B]/10 text-[#4A708B]'
                              : 'bg-[#D4AF37]/10 text-[#D4AF37]'
                          }`}>
                            {donation.donation_type === 'monthly' ? text.monthly : text.single}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Heart className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
                      <p className="text-[#546A76]">{text.noDonations}</p>
                      <Button
                        onClick={() => navigate('/donate')}
                        className="mt-4 gold-btn rounded-full"
                      >
                        {text.startDonating}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Donations Tab */}
            {activeTab === 'donations' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="font-serif text-xl font-semibold text-[#1A252C] mb-6">
                  {text.donations}
                </h2>
                <DonationsTable 
                  donations={donations}
                  text={text}
                  formatDate={formatDate}
                  formatAmount={formatAmount}
                  onDownloadReceipt={handleDownloadReceipt}
                />
              </div>
            )}

            {/* Receipts Tab */}
            {activeTab === 'receipts' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                {/* Annual Receipts Section */}
                {annualReceipts.filter((y) => y.eligible).length > 0 && (
                  <div className="mb-8" data-testid="annual-receipts-section">
                    <h2 className="font-serif text-xl font-semibold text-[#1A252C] mb-2">
                      {language === 'fr' ? 'Reçus fiscaux annuels' : 'Annual Tax Receipts'}
                    </h2>
                    <p className="text-sm text-[#546A76] mb-4">
                      {language === 'fr'
                        ? 'Un seul reçu regroupant tous vos dons de l\'année civile — idéal pour votre déclaration de revenus.'
                        : 'A single receipt covering all your donations for the calendar year — ideal for your tax return.'}
                    </p>
                    <div className="space-y-3">
                      {annualReceipts.filter((y) => y.eligible).map((y) => (
                        <div
                          key={`annual-${y.year}`}
                          data-testid={`annual-receipt-${y.year}`}
                          className="flex items-center justify-between p-4 rounded-xl border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                              <FileText className="w-6 h-6 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#1A252C]">
                                {language === 'fr' ? `Reçu annuel ${y.year}` : `Annual receipt ${y.year}`}
                              </p>
                              <p className="text-sm text-[#546A76]">
                                {y.count} {language === 'fr' ? (y.count > 1 ? 'dons' : 'don') : (y.count > 1 ? 'donations' : 'donation')} — {formatAmount(y.total)}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDownloadAnnualReceipt(y.year, y.receipt_number)}
                            data-testid={`download-annual-receipt-${y.year}`}
                            className="gold-btn rounded-full"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {language === 'fr' ? 'Télécharger' : 'Download'}
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="border-b border-[#E2E8F0] mt-8"></div>
                  </div>
                )}

                <h2 className="font-serif text-xl font-semibold text-[#1A252C] mb-2">
                  {text.receipts}
                </h2>
                <p className="text-sm text-[#546A76] mb-6">{text.receiptsNote}</p>
                <ReceiptsList
                  receipts={receipts}
                  text={text}
                  formatDate={formatDate}
                  formatAmount={formatAmount}
                  onDownload={handleDownloadReceipt}
                  onResend={handleResendReceipt}
                />
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="font-serif text-xl font-semibold text-[#1A252C] mb-6">
                  {text.subscriptions}
                </h2>
                <SubscriptionsList
                  subscriptions={subscriptions}
                  text={text}
                  formatDate={formatDate}
                  formatAmount={formatAmount}
                />
              </div>
            )}

            {/* Public Profile Tab */}
            {activeTab === 'public' && (
              <PublicProfileForm
                publicProfile={publicProfile}
                setPublicProfile={setPublicProfile}
                profile={profile}
                stats={stats}
                text={text}
                language={language}
                onSubmit={handlePublicProfileUpdate}
                isUpdating={updatingPublicProfile}
              />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && profile && (
              <ProfileSettingsForm
                profile={profile}
                setProfile={setProfile}
                passwordData={passwordData}
                setPasswordData={setPasswordData}
                text={text}
                onProfileSubmit={handleProfileUpdate}
                onPasswordSubmit={handlePasswordChange}
                isUpdatingProfile={updatingProfile}
                isChangingPassword={changingPassword}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonorDashboardPage;
